/**
 * parcoursData.js
 * Définition des 5 parcours client de Body-Up.
 * Chaque parcours est ensuite rendu par <ParcoursPage /> dans un style "Wahou"
 * (hero gradient + section badge + grille d'étapes + sections additionnelles).
 */
import {
  Flame, Dumbbell, ShieldCheck, RefreshCw, Globe,
  ClipboardCheck, Apple, Activity, ScanLine, TrendingUp,
  Target, MessageSquare, Video, Smartphone, Calendar, Zap, Heart,
} from 'lucide-react';

export const PARCOURS = [
  // 1. PERTE DE GRAS
  {
    slug: 'perte-de-gras',
    emoji: '🔥',
    label: 'Perte de gras',
    tagline: 'Affine ta silhouette, préserve ton muscle.',
    description:
      "Un parcours pensé pour faire baisser ta masse grasse sans casser ta dynamique musculaire. On combine nutrition en déficit maîtrisé, entraînement adapté et mesures objectives via scan 3D.",
    gradient: 'from-rose-500 via-pink-500 to-primary',
    accentClass: 'text-rose-400',
    glowRgba: 'rgba(244, 63, 94, 0.35)',
    icon: Flame,
    duration: '12 semaines+',
    sessions: '1 à 3×/sem',
    forWho: [
      "Perdre 5 à 15 kg de gras",
      "Visualiser sa progression avec des données objectives",
      "Préserver son tonus musculaire pendant la perte",
    ],
    steps: [
      {
        icon: ClipboardCheck,
        title: 'Bilan gratuit + Scan 3D Styku',
        desc: "On définit ton point de départ avec un scan 3D complet, des photos repères et un échange sur ton historique et tes contraintes.",
      },
      {
        icon: Apple,
        title: 'Plan nutrition en déficit',
        desc: "Calcul précis de tes calories et macros via le calculateur Body-Up, puis plan alimentaire personnalisé adapté à ton mode de vie.",
      },
      {
        icon: Activity,
        title: 'Personal Training ou EMS Training',
        desc: "1 à 3 séances par semaine combinant cardio et musculation (ou EMS) pour préserver ton muscle pendant la perte de gras.",
      },
      {
        icon: ScanLine,
        title: 'Scan Styku trimestriel + ajustements',
        desc: "Nouveau scan tous les 3 mois pour mesurer la baisse de masse grasse et ajuster nutrition + entraînement.",
      },
    ],
    cta: 'Démarrer mon parcours perte de gras',
  },

  // 2. PRISE DE MUSCLE
  {
    slug: 'prise-de-muscle',
    emoji: '💪',
    label: 'Prise de muscle',
    tagline: 'Construis du muscle, gagne en force et en densité.',
    description:
      "Un parcours hypertrophie progressive pensé pour développer ta masse musculaire, ta force et ton athlétisme. Programme structuré, nutrition adaptée et suivi technique 1:1.",
    gradient: 'from-primary via-pink-500 to-purple-500',
    accentClass: 'text-primary',
    glowRgba: 'rgba(185, 32, 116, 0.4)',
    icon: Dumbbell,
    duration: '16 semaines+',
    sessions: '1 à 3×/sem',
    badge: 'LE PLUS DEMANDÉ',
    forWho: [
      "Prendre 3 à 8 kg de muscle propre",
      "Gagner en force et en performance",
      "Sculpter une silhouette plus dense",
    ],
    steps: [
      {
        icon: ScanLine,
        title: 'Bilan 3D Styku + objectifs',
        desc: "Bilan complet : scan 3D, objectifs de force et volume, mesures repères, photos initiales.",
      },
      {
        icon: Apple,
        title: 'Plan nutrition en surplus contrôlé',
        desc: "Surplus calorique progressif et apport protéique optimisé pour favoriser la construction musculaire sans excès de gras.",
      },
      {
        icon: Dumbbell,
        title: 'Personal Training 1 à 3×/sem',
        desc: "Programme d'hypertrophie progressive structuré en cycles, avec une attention maximale à la technique et à la surcharge progressive.",
      },
      {
        icon: TrendingUp,
        title: 'Suivi de charges et technique 1:1',
        desc: "Tracking précis des charges, séries et répétitions. Coaching technique poussé sur chaque mouvement clé.",
      },
      {
        icon: ScanLine,
        title: 'Scan Styku trimestriel + ajustement',
        desc: "Mesure de la progression musculaire tous les 3 mois et ajustement du programme pour rester sur la trajectoire.",
      },
    ],
    cta: 'Démarrer mon parcours prise de muscle',
  },

  // 3. DIMINUTION DOULEURS DORSALES (EMS + posture)
  {
    slug: 'diminution-douleurs-dorsales',
    emoji: '🛡️',
    label: 'Diminution des douleurs dorsales',
    tagline: 'Soulage ton dos, retrouve une posture solide.',
    description:
      "Un parcours dédié aux personnes qui souffrent de mal de dos, d'articulations ou d'un déséquilibre postural. On utilise l'EMS pour activer en profondeur les muscles stabilisateurs sans surcharge articulaire.",
    gradient: 'from-amber-400 via-orange-500 to-rose-500',
    accentClass: 'text-amber-400',
    glowRgba: 'rgba(251, 191, 36, 0.35)',
    icon: ShieldCheck,
    duration: '8 semaines+',
    sessions: '1 à 2×/sem',
    forWho: [
      "Douleurs dorsales ou articulaires chroniques",
      "Posture affaissée (travail de bureau, écran)",
      "Manque de temps pour s'entraîner en salle classique",
    ],
    steps: [
      {
        icon: ScanLine,
        title: 'Bilan postural 3D + adaptation',
        desc: "Analyse posturale 3D détaillée, identification des déséquilibres, prise en compte des douleurs et contraintes de temps.",
      },
      {
        icon: Zap,
        title: 'Séances EMS 20-25 min 1 à 2×/sem',
        desc: "Électrostimulation guidée en studio privé. Travail ciblé sur les muscles profonds sans charge articulaire.",
      },
      {
        icon: Heart,
        title: 'Renforcement profond ciblé',
        desc: "Gainage, lombaires, scapulas, mobilité hanches/épaules. Conseils nutrition simples pour soutenir la récupération.",
      },
      {
        icon: TrendingUp,
        title: 'Suivi mensuel + ajustements',
        desc: "Bilan mensuel pour évaluer la diminution des douleurs et adapter l'intensité de l'EMS et les exercices.",
      },
    ],
    cta: 'Démarrer mon parcours dos & posture',
  },

  // 4. COACHING HYBRIDE
  {
    slug: 'coaching-hybride',
    emoji: '🔄',
    label: 'Coaching Hybride',
    tagline: 'Le meilleur des deux mondes : présentiel + autonomie.',
    description:
      "Un programme complet de 6 mois qui mélange coaching présentiel chez Body-Up pour la maîtrise technique parfaite, et autonomie guidée via l'app pour avancer entre les séances. Avec un suivi WhatsApp hebdomadaire et un bilan avant/après par scan 3D.",
    gradient: 'from-cyan-400 via-blue-500 to-indigo-600',
    accentClass: 'text-cyan-400',
    glowRgba: 'rgba(6, 182, 212, 0.35)',
    icon: RefreshCw,
    duration: '6 mois (24 semaines)',
    sessions: '6 séances présentiel 60 min + autonomie app',
    price: '399 CHF/mois',
    forWho: [
      "Veulent un suivi humain régulier sans coaching présentiel hebdo",
      "Veulent maîtriser parfaitement les exercices avant d'être autonomes",
      "Cherchent une transformation mesurable avec scan 3D avant/après",
    ],
    steps: [
      {
        icon: ScanLine,
        title: 'Scan 3D Styku initial (30 min)',
        desc: "Analyse posturale 3D complète + explication de tes résultats. Ton point de départ chiffré et visualisé.",
      },
      {
        icon: Dumbbell,
        title: '3 séances présentiel 60 min (semaines 1-12)',
        desc: "Réparties sur les 12 premières semaines pour que tu maîtrises parfaitement chaque exercice de ton programme.",
      },
      {
        icon: Smartphone,
        title: 'Programme + nutrition + suivi via app',
        desc: "Programme musculation évolutif sur l'app, plan nutrition personnalisé, suivi des macros et WhatsApp hebdomadaire avec ton coach.",
      },
      {
        icon: Calendar,
        title: '3 séances présentiel 60 min (semaines 13-24)',
        desc: "Programme évolutif sur les 12 semaines suivantes : progressions techniques, intensification, ajustements en présentiel.",
      },
      {
        icon: ScanLine,
        title: 'Scan 3D Styku final (30 min)',
        desc: "Bilan complet avant/après avec analyse détaillée de ta transformation : masse grasse, muscle, posture, mesures.",
      },
    ],
    cta: 'Démarrer mon coaching hybride (399 CHF/mois)',
  },

  // 5. COACHING 100% EN LIGNE
  {
    slug: 'coaching-online',
    emoji: '🌐',
    label: 'Coaching 100% en ligne',
    tagline: "Coaching premium, accessible partout dans le monde.",
    description:
      "Un parcours 100% à distance pour les expatriés, les voyageurs ou ceux qui ne sont pas à Nyon. On-boarding visio approfondi, programme personnalisé sur app, suivi WhatsApp 7/7 et Zoom mensuel d'ajustement avec ton coach.",
    gradient: 'from-emerald-400 via-teal-500 to-cyan-600',
    accentClass: 'text-emerald-400',
    glowRgba: 'rgba(16, 185, 129, 0.35)',
    icon: Globe,
    duration: '6 mois minimum',
    sessions: 'Programme à dérouler à ton rythme',
    price: '299 CHF/mois',
    forWho: [
      "Expatriés / voyageurs fréquents",
      "Pas à Nyon mais veulent un coach humain Body-Up",
      "Préfèrent l'autonomie avec un cadre solide et un suivi régulier",
    ],
    steps: [
      {
        icon: Video,
        title: 'Zoom on-boarding 60 min',
        desc: "Visio complète avec ton coach : objectifs précis, historique, photos initiales et mesures pour définir un point de départ chiffré.",
      },
      {
        icon: Smartphone,
        title: "Programme d'entraînement sur app",
        desc: "Programme personnalisé à dérouler en salle ou chez toi, accessible depuis ton smartphone, vidéos d'exécution pour chaque exercice.",
      },
      {
        icon: Apple,
        title: 'Plan nutrition + suivi macros via app',
        desc: "Plan alimentaire adapté à ton environnement et tes contraintes, suivi quotidien des macros dans l'app.",
      },
      {
        icon: MessageSquare,
        title: 'WhatsApp 7/7 + Zoom 30 min/mois',
        desc: "Canal WhatsApp ouvert 7j/7 pour les questions et la motivation + visio mensuelle de 30 min pour ajuster le programme.",
      },
      {
        icon: TrendingUp,
        title: 'Bilan mensuel avec photos comparatives',
        desc: "Photos avant/après mensuelles, mesures, et adaptation complète du programme pour rester sur la trajectoire.",
      },
    ],
    cta: 'Démarrer mon coaching en ligne (299 CHF/mois)',
  },
];

export const getParcoursBySlug = (slug) => PARCOURS.find((p) => p.slug === slug);
